import React, { useState } from 'react'
import Button from 'components/ds/Button'
import Badge from 'components/ds/Badge'
import Icon from 'components/ds/Icon'
import { ChoiceGroup, Radio } from 'components/ds/Choice'
import { Input } from 'components/ds/Input'
import { IdentityCard, HitRow } from './index'
import styles from './SourceTabs.module.scss'

/**
 * Healthcare Search results split into one tab per data source, OFAC first. Cards are identities
 * merged across sources, so a card shows in every tab it has a hit in, with only that source's hits.
 * Used by the application drawer (with decisions) and Quick Search (read only).
 */

export const TAB_SOURCES = [
    { key: 'OFAC', label: 'OFAC', name: 'OFAC SDN', blurb: 'U.S. Treasury sanctions list, including a.k.a. names' },
    { key: 'LEIE', label: 'LEIE', name: 'HHS OIG LEIE', blurb: 'Federal health care exclusions' },
    { key: 'SAMGOV', label: 'SAM', name: 'SAM.gov', blurb: 'Government-wide exclusions' },
    { key: 'STATE_MEDICAID', label: 'Medicaid', name: 'State Medicaid', blurb: 'State Medicaid exclusion lists' },
    { key: 'NPPES', label: 'NPPES', name: 'NPPES', blurb: 'Provider directory. Identity only, not an exclusion list.' },
]

const RISK_SOURCES = ['LEIE', 'SAMGOV', 'STATE_MEDICAID']

const cx = (...p) => p.filter(Boolean).join(' ')
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
const fmtDateTime = (iso) => (iso
    ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '')

export const hasOfacHit = (card) => (card?.hits || []).some(h => h.source === 'OFAC')
export const isRiskCard = (card) => card?.status === 'EXCLUDED' || card?.status === 'PRIOR_SANCTIONS'

const cardsFor = (cards, source) => cards.filter(c => (c.hits || []).some(h => h.source === source))

// Opens where the attention is: OFAC when it matched or failed, else the first exclusion list with a
// match, else OFAC so its clear result is the first thing read.
export const defaultTab = (response) => {
    const cards = response?.cards || []
    const ofac = (response?.sources || []).find(s => s.source === 'OFAC')
    if (ofac?.status === 'ERROR' || cardsFor(cards, 'OFAC').length) return 'OFAC'
    return RISK_SOURCES.find(s => cardsFor(cards, s).length) || 'OFAC'
}

// OFAC ships the program as "SDGT] [IRAN"; one tag per program.
export const programsOf = (raw) => (raw || '').split(/[[\];,]+/).map(p => p.trim()).filter(Boolean)

const ALIAS_LABELS = { aka: 'a.k.a. alias', fka: 'former name (f.k.a.)', nka: 'new name (n.k.a.)' }
export const aliasLabel = (type) => ALIAS_LABELS[(type || '').toLowerCase().replace(/[^a-z]/g, '')] || 'alias'

// Searches stored before the OFAC fields were split out only carry "Matched a.k.a.: NAME · remarks".
export const ofacDetails = (hit) => {
    const m = /^Matched ([^:]+): ([^·]+?)(?:\s·\s(.*))?$/s.exec(hit.reason || '')
    return {
        matchedName: hit.matchedName || (m ? m[2].trim() : null),
        matchedNameType: hit.matchedNameType || (m ? m[1] : null),
        remarks: hit.remarks || (m ? m[3] || null : hit.reason || null),
    }
}

export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// Same words, ignoring case, punctuation and order: "Torres Sosa, Benjamin" = "TORRES SOSA, Benjamin".
const normName = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9\s]/g, ' ').split(/\s+/).filter(Boolean).sort().join(' ')

// OFAC matches any listed name that contains every searched word; an exact match is the searched
// name equal to the primary name or the alias it matched.
export const isExactOfac = (hit, searchedName) => {
    const q = normName(searchedName)
    if (!q || hit?.source !== 'OFAC') return false
    return [hit.displayName, ofacDetails(hit).matchedName].some(n => n && normName(n) === q)
}

// Words of the listed name the search didn't cover, in listing order. OFAC matches inside words, so
// "Benja" covers "BENJAMIN".
export const extraWords = (listed, searchedName) => {
    const searched = normName(searchedName).split(' ').filter(Boolean)
    return (listed || '').toUpperCase().replace(/[^A-Z0-9\s]/g, ' ').split(/\s+/)
        .filter(w => w && !searched.some(s => w.includes(s)))
}

export const ofacExactCount = (response, searchedName) => (response?.cards || [])
    .filter(c => (c.hits || []).some(h => isExactOfac(h, searchedName))).length

// The health care exclusion lists carry NPIs, so an exact match there is the same NPI as searched,
// or the same full name (same words, any order).
export const isExactListHit = (hit, searchedName, searchedNpi) => {
    if (!RISK_SOURCES.includes(hit?.source)) return false
    const npi = (searchedNpi || '').trim()
    if (npi && hit.npi && hit.npi.trim() === npi) return true
    const q = normName(searchedName)
    return !!q && normName(hit.displayName) === q
}

// Any exact match on any list: OFAC by name, the exclusion lists by NPI or name.
export const isExactHit = (hit, searchedName, searchedNpi) => (hit?.source === 'OFAC'
    ? isExactOfac(hit, searchedName)
    : isExactListHit(hit, searchedName, searchedNpi))

// First exclusion list (LEIE, SAM, Medicaid order) with an exact match, or null. OFAC is checked
// separately because it always comes first.
export const listExactSource = (response, searchedName, searchedNpi) => {
    const cards = response?.cards || []
    return TAB_SOURCES.find(s => RISK_SOURCES.includes(s.key) && cards.some(c => (c.hits || [])
        .some(h => h.source === s.key && isExactListHit(h, searchedName, searchedNpi)))) || null
}

// Each list is ruled out on what it actually carries: OFAC on DOB, nationality and ID numbers in its
// remarks; the health care exclusion lists on NPI, state, DOB and provider type.
const REVIEW_REASONS = {
    OFAC: ['Different date of birth', 'Different nationality', 'Different ID number', 'Different person or business type', 'Other'],
    DEFAULT: ['Different NPI', 'Different state', 'Different date of birth', 'Different provider type', 'Other'],
}

// Open matches per exclusion list, for the drawer's "to review" summary.
export const openBySource = (response, decisionOf) => TAB_SOURCES
    .filter(s => s.key !== 'NPPES')
    .map(s => ({ ...s, open: cardsFor(response?.cards || [], s.key).filter(c => isRiskCard(c) && !decisionOf(c)).length }))
    .filter(s => s.open)

// `tab` / `onTab` make the selected tab controllable, so the drawer's summary can jump to a list.
const SourceTabs = ({
    response, searchedName, searchedNpi, searchedAt, entityName, readOnly,
    decisionOf, onConfirm, onUndo, onRerun, onMarkReviewed, tab: tabProp, onTab,
}) => {
    const [ownTab, setOwnTab] = useState(() => defaultTab(response))
    const tab = tabProp || ownTab
    const setTab = onTab || setOwnTab
    const cards = response?.cards || []
    const sources = response?.sources || []
    const decision = (card) => (decisionOf ? decisionOf(card) : null)

    const summarize = (src) => {
        const s = sources.find(x => x.source === src.key)
        const list = cardsFor(cards, src.key)
        if (!s) return { text: '—' }
        if (s.status === 'ERROR') return { text: 'Error', tone: 'risk', attention: true }
        if (s.status === 'DISABLED') return { text: 'Off' }
        if (src.key === 'NPPES') return { text: list.length ? plural(list.length, 'record', 'records') : 'No record' }
        if (!list.length) return { text: 'No match' }
        const decisions = list.map(decision)
        // Red only for an exact OFAC name match; partial OFAC matches look like any other review.
        const exact = list.some(c => (c.hits || []).some(h => h.source === src.key && isExactHit(h, searchedName, searchedNpi)))
        const tone = exact ? 'sanction' : 'risk'
        // What is still open leads; once nothing is, the tab says what was decided.
        const open = decisions.filter(d => !d).length
        if (open) return { text: open === list.length ? plural(open, 'match', 'matches') : `${open} to review`, tone, attention: true }
        if (decisions.some(d => d.decision !== 'DISMISSED')) return { text: 'Saved', tone: exact ? 'sanction' : undefined }
        return { text: 'Reviewed' }
    }

    const active = TAB_SOURCES.find(s => s.key === tab)
    return (
        <div className={styles.root}>
            <div className={styles.tabs} role='tablist'>
                {TAB_SOURCES.map(src => {
                    const sum = summarize(src)
                    return (
                        <button key={src.key} type='button' role='tab' aria-selected={tab === src.key}
                                className={cx(styles.tab, tab === src.key && styles.tabActive,
                                    sum.tone === 'sanction' && styles.tabSanction, sum.tone === 'risk' && styles.tabRisk,
                                    sum.attention && styles.tabAttention)}
                                onClick={() => setTab(src.key)}>
                            <span className={styles.tabLabel}>{src.label}</span>
                            <span className={styles.tabCount}>{sum.text}</span>
                        </button>
                    )
                })}
            </div>
            <SourcePanel
                key={active.key}
                src={active}
                status={sources.find(s => s.source === active.key)}
                cards={cardsFor(cards, active.key)}
                searchedName={searchedName}
                searchedNpi={searchedNpi}
                searchedAt={searchedAt}
                entityName={entityName}
                readOnly={readOnly}
                decision={decision}
                onConfirm={onConfirm}
                onUndo={onUndo}
                onRerun={onRerun}
                onMarkReviewed={onMarkReviewed}
            />
        </div>
    )
}

// Review one list at a time: rule out its open matches with a reason that fits that list, or show
// who reviewed it once nothing is open.
const TabReview = ({ src, cards, entityName, decision, onMarkReviewed, onUndo }) => {
    const reasons = REVIEW_REASONS[src.key] || REVIEW_REASONS.DEFAULT
    const [form, setForm] = useState(false)
    const [reason, setReason] = useState(reasons[0])
    const [other, setOther] = useState('')
    const risk = cards.filter(isRiskCard)
    const open = risk.filter(c => !decision(c))
    const ruledOut = risk.map(decision).filter(d => d && d.decision === 'DISMISSED')
    const chosen = reason === 'Other' ? other.trim() : reason
    const what = `${open.length} open ${src.name} ${open.length === 1 ? 'match' : 'matches'}`

    if (!open.length) {
        if (!ruledOut.length) return null
        const last = ruledOut.slice().sort((a, b) => (b.decidedAt || '').localeCompare(a.decidedAt || ''))[0]
        return (
            <div className={styles.reviewLine}>
                <span>
                    <strong>Reviewed</strong>{last.decidedBy ? ` by ${last.decidedBy}` : ''}
                    {last.decidedAt ? ` · ${fmtDateTime(last.decidedAt)}` : ''}{last.notes ? ` · ${last.notes}` : ''}
                </span>
                <UndoConfirm question='Undo the review? These matches go back to open.' onUndo={() => onUndo(ruledOut)} />
            </div>
        )
    }
    if (!form) {
        return (
            <div className={styles.reviewLine}>
                <span>Mark reviewed if none of the {what} are {entityName || 'this provider'}.</span>
                <Button variant='secondary' size='small' onClick={() => setForm(true)}>Mark reviewed</Button>
            </div>
        )
    }
    return (
        <div className={styles.reviewForm}>
            <strong>Mark the {what} reviewed?</strong>
            <span className={styles.hint}>Recorded with your name, the time and the reason. Other lists are not affected.</span>
            <ChoiceGroup legend='Reason'>
                {reasons.map(r => (
                    <Radio key={r} name={`review-reason-${src.key}`} label={r} checked={reason === r} onChange={() => setReason(r)} />
                ))}
            </ChoiceGroup>
            {reason === 'Other' && (
                <Input label='Reason' value={other} maxLength={500} onChange={(ev) => setOther(ev.target.value)} />
            )}
            <div className={styles.ofacActions}>
                <Button variant='secondary' size='small' onClick={() => setForm(false)}>Cancel</Button>
                <Button variant='primary' size='small' disabled={!chosen}
                        onClick={() => { setForm(false); onMarkReviewed(open, chosen) }}>
                    Mark reviewed
                </Button>
            </div>
        </div>
    )
}

const SourcePanel = ({
    src, status, cards, searchedName, searchedNpi, searchedAt, entityName, readOnly, decision, onConfirm, onUndo, onRerun, onMarkReviewed,
}) => {
    const isOfac = src.key === 'OFAC'
    // Exact matches lead (OFAC by name, the other lists by NPI or name); the rest keep their order.
    const isExactHere = (c) => (c.hits || []).some(h => h.source === src.key && isExactHit(h, searchedName, searchedNpi))
    cards = [...cards.filter(isExactHere), ...cards.filter(c => !isExactHere(c))]
    // Several OFAC matches show collapsed, one open at a time: the first still undecided.
    const [openKey, setOpenKey] = useState(() => (cards.find(c => !decision(c)) || cards[0])?.matchedKey)
    const count = src.key === 'NPPES'
        ? plural(cards.length, 'identity record', 'identity records')
        : cards.length ? plural(cards.length, 'possible match', 'possible matches') : 'No match'

    return (
        <div className={styles.panel}>
            <div className={styles.panelHead}>
                <div>
                    <div className={styles.panelTitle}>{src.name}</div>
                    <div className={styles.hint}>{src.blurb}</div>
                </div>
                {status?.status === 'OK' && <span className={styles.hint}>{count}</span>}
            </div>

            {status?.status === 'OK' && !readOnly && onMarkReviewed && src.key !== 'NPPES' && (
                <TabReview src={src} cards={cards} entityName={entityName} decision={decision}
                           onMarkReviewed={onMarkReviewed} onUndo={onUndo} />
            )}

            {status?.status === 'ERROR' && (
                <div className={styles.errorBox}>
                    <div className={styles.errorTitle}><Icon name='alert-triangle' size={16} /> {src.name} did not respond</div>
                    <div>
                        {entityName ? `${entityName} has not been checked against this list. ` : 'Not checked against this list. '}
                        The other sources returned results. Search again to check it; a search re-runs all five sources.
                    </div>
                    {onRerun && (
                        <Button variant='secondary' size='small' startIcon={<Icon name='refresh-cw' size={14} />} onClick={onRerun}>
                            Search again
                        </Button>
                    )}
                </div>
            )}
            {status?.status === 'DISABLED' && <div className={styles.hint}>Not enabled for this client.</div>}
            {!status && <div className={styles.hint}>This source wasn't part of this search.</div>}

            {status?.status === 'OK' && src.key === 'STATE_MEDICAID' && <MedicaidCoverage status={status} />}

            {status?.status === 'OK' && !cards.length && (
                <div className={styles.clearLine}>
                    <Icon name='check-circle' size={16} />
                    <span>
                        {src.key === 'NPPES' ? 'No NPPES record' : isOfac ? 'No OFAC sanctions match' : `No match on ${src.name}`}
                        {searchedAt && <span className={styles.hint}> · checked {fmtDateTime(searchedAt)}{isOfac ? ', incl. a.k.a. names' : ''}</span>}
                    </span>
                </div>
            )}

            {status?.status === 'OK' && cards.map(card => (isOfac ? (
                <OfacMatch
                    key={card.matchedKey}
                    card={card}
                    record={decision(card)}
                    searchedName={searchedName}
                    entityName={entityName}
                    readOnly={readOnly}
                    collapsible={cards.length > 1}
                    open={cards.length === 1 || openKey === card.matchedKey}
                    onToggle={() => setOpenKey(k => (k === card.matchedKey ? null : card.matchedKey))}
                    onConfirm={onConfirm}
                    onUndo={onUndo}
                />
            ) : (
                <IdentityCard
                    key={card.matchedKey}
                    card={card}
                    defaultExpanded={src.key !== 'NPPES'}
                    detail={(card.hits || []).filter(h => h.source === src.key).map((h, i) => <HitRow key={i} hit={h} />)}
                    actions={readOnly || src.key === 'NPPES' ? null : (
                        <DecisionActions record={decision(card)} exact={isExactHere(card)} onConfirm={() => onConfirm(card)} onUndo={onUndo} />
                    )}
                />
            )))}
        </div>
    )
}

// Undo asks once before it acts: it removes a saved record or a review for everyone on the application.
const UndoConfirm = ({ question, onUndo }) => {
    const [asking, setAsking] = useState(false)
    if (!asking) return <button type='button' className={styles.linkBtn} onClick={() => setAsking(true)}>Undo</button>
    return (
        <span className={styles.undoConfirm}>
            <span>{question}</span>
            <Button variant='secondary' size='small' onClick={() => setAsking(false)}>Cancel</Button>
            <Button variant='primary' size='small' onClick={() => { setAsking(false); onUndo() }}>Undo</Button>
        </span>
    )
}

// Saving keeps the drawer and the card open; the button holds "Saving…" until the record shows up.
const useSaving = (record) => {
    const [saving, setSaving] = useState(false)
    const save = (fn) => {
        setSaving(true)
        Promise.resolve(fn()).then(ok => { if (ok === false) setSaving(false) })
    }
    return [saving && !record, save]
}

export const DecisionActions = ({ record, exact, onConfirm, onUndo }) => {
    const [saving, save] = useSaving(record)
    if (record && record.decision !== 'DISMISSED') {
        return (
            <div className={styles.decided}>
                <span>Saved{record.decidedBy ? ` by ${record.decidedBy}` : ''}</span>
                <UndoConfirm question='Undo the saved record?' onUndo={() => onUndo(record)} />
            </div>
        )
    }
    if (record) return <span className={styles.hint}>Reviewed</span>
    return (
        <div className={styles.decided}>
            {exact && <Badge tone='declined' title='Same NPI or same full name as the search.'>Exact match</Badge>}
            <Button variant='secondary' size='small' loading={saving} disabled={saving} onClick={() => save(onConfirm)}>
                Save record
            </Button>
        </div>
    )
}

const MedicaidCoverage = ({ status }) => {
    const covered = status.coveredStates || []
    const missing = status.notCoveredStates || []
    if (!covered.length && !missing.length) return null
    return (
        <div className={styles.hint}>
            Checked {plural(covered.length, 'state', 'states')}{missing.length ? `; no list for ${missing.join(', ')}` : ''}.
        </div>
    )
}

const Field = ({ label, children }) => (children == null || children === '' ? null : (
    <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <span>{children}</span>
    </div>
))

// `calm`: settled without its own decision (another record on this list was saved), so shown in grey.
export const OfacMatch = ({
    card, record, searchedName, entityName, readOnly, collapsible, open, onToggle, onConfirm, onUndo, calm,
}) => {
    const [confirming, setConfirming] = useState(false)
    const [allRemarks, setAllRemarks] = useState(false)
    const hit = (card.hits || []).find(h => h.source === 'OFAC') || {}
    const { matchedName, matchedNameType, remarks } = ofacDetails(hit)
    const programs = programsOf(hit.exclusionProgram)
    const type = cap(hit.exclusionType) || (card.entity ? 'Entity' : 'Individual')
    const confirmed = record && record.decision !== 'DISMISSED'
    const [saving, save] = useSaving(record)
    const exact = isExactOfac(hit, searchedName)
    // Red = on the list (saved, or an exact match to check); amber = a similar name to check; grey = settled.
    const settled = !confirmed && (record || calm)
    const badge = confirmed ? { tone: 'declined', text: 'Saved record' }
        : settled ? { tone: 'closed', text: exact ? 'Exact match' : 'Partial name match' }
        : exact ? { tone: 'declined', text: 'Exact match' }
        : { tone: 'watch', text: 'Partial name match' }
    // One line: an exact match says so; a partial one names the listed words you didn't search.
    const extra = extraWords(matchedName || hit.displayName, searchedName)
    const why = exact
        ? 'Exact name match. Compare date of birth, nationality and ID numbers in the remarks before saving.'
        : extra.length
            ? `Partial: "${extra.join(' ')}" ${extra.length === 1 ? 'is' : 'are'} not in your search. Check the remarks before saving.`
            : 'Partial name match. Check the remarks before saving.'

    return (
        <div className={cx(styles.ofacCard, !settled && !confirmed && !exact && styles.ofacCardPartial,
            settled && styles.ofacCardCalm, confirmed && styles.ofacCardConfirmed)}>
            <div className={cx(styles.ofacHead, collapsible && styles.clickable)}
                 role={collapsible ? 'button' : undefined}
                 tabIndex={collapsible ? 0 : undefined}
                 aria-expanded={collapsible ? open : undefined}
                 onClick={collapsible ? onToggle : undefined}
                 onKeyDown={collapsible ? (ev) => {
                     if (ev.key === 'Enter' || ev.key === ' ') {
                         ev.preventDefault()
                         onToggle()
                     }
                 } : undefined}>
                <div className={styles.ofacTitle}>
                    <span className={styles.ofacName}>{hit.displayName || card.displayName}</span>
                    <span className={styles.hint}>
                        {matchedName ? `${cap(aliasLabel(matchedNameType))}: ${matchedName}` : 'Sanctioned name'} · {type}
                    </span>
                </div>
                <div className={styles.ofacBadges}>
                    <Badge tone={badge.tone}>{badge.text}</Badge>
                    {collapsible && <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} />}
                </div>
            </div>

            {!open && (
                <div className={styles.ofacSummary}>
                    <div className={styles.tags}>
                        {programs.map(p => <span key={p} className={styles.tag}>{p}</span>)}
                        {hit.country && <span className={styles.hint}>{hit.country}</span>}
                    </div>
                    {!record && <div className={styles.hint}>{why}</div>}
                </div>
            )}

            {open && (
                <>
                    <div className={styles.why}>
                        <div className={styles.whyTitle}>Why it matched</div>
                        <div className={styles.whyGrid}>
                            <div>
                                <div className={styles.fieldLabel}>You searched</div>
                                <div className={styles.whyName}>{searchedName || '—'}</div>
                            </div>
                            <span className={styles.whyArrow} aria-hidden>→</span>
                            <div>
                                <div className={styles.fieldLabel}>
                                    {matchedName ? `Matched ${aliasLabel(matchedNameType)}` : 'Matched primary name'}
                                </div>
                                <div className={styles.whyName}>{matchedName || hit.displayName}</div>
                            </div>
                        </div>
                        <div className={styles.hint}>{why}</div>
                    </div>

                    <div className={styles.fields}>
                        {programs.length > 0 && (
                            <Field label={programs.length > 1 ? `Sanctions programs (${programs.length})` : 'Sanctions program'}>
                                <span className={styles.tags}>{programs.map(p => <span key={p} className={styles.tag}>{p}</span>)}</span>
                            </Field>
                        )}
                        <Field label='Type'>{type}</Field>
                        <Field label='Title'>{hit.title || 'None listed'}</Field>
                        <Field label='Address'>{hit.address || 'None listed'}</Field>
                    </div>

                    {remarks && (
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Remarks</span>
                            {/* Only long remarks are clamped (with a way to expand); short ones always show in full. */}
                            <span className={allRemarks || remarks.length <= 160 ? undefined : styles.clamp}>{remarks}</span>
                            {remarks.length > 160 && (
                                <button type='button' className={styles.linkBtn} onClick={() => setAllRemarks(v => !v)}>
                                    {allRemarks ? 'Show less' : 'Show full remarks'}
                                </button>
                            )}
                        </div>
                    )}

                    {!readOnly && !record && !confirming && (
                        <div className={styles.ofacActions}>
                            <Button variant='secondary' size='small' loading={saving} disabled={saving}
                                    onClick={() => setConfirming(true)}>
                                Save record
                            </Button>
                        </div>
                    )}
                    {!readOnly && confirming && !record && (
                        <div className={styles.confirmBox}>
                            <div className={styles.confirmTitle}><Icon name='alert-triangle' size={16} /> Save this OFAC match as a record?</div>
                            <div>
                                {entityName || 'This entity'} will be marked Sanctioned on this deal. The record is saved with your name and the time.
                            </div>
                            <div className={styles.ofacActions}>
                                <Button variant='secondary' size='small' onClick={() => setConfirming(false)}>Cancel</Button>
                                <Button variant='primary' size='small' onClick={() => { setConfirming(false); save(() => onConfirm(card)) }}>
                                    Save record
                                </Button>
                            </div>
                        </div>
                    )}
                    {!readOnly && confirmed && (
                        <div className={styles.confirmedLine}>
                            <span>
                                Saved{record.decidedBy ? ` by ${record.decidedBy}` : ''}
                                {record.decidedAt ? ` · ${fmtDateTime(record.decidedAt)}` : ''}.
                                {' '}{entityName || 'This entity'} is marked Sanctioned.
                            </span>
                            <UndoConfirm question='Undo the saved record?' onUndo={() => onUndo(record)} />
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default SourceTabs
